import { mode, StyleFunctionProps } from '@chakra-ui/theme-tools';
import { rgba } from 'polished';

import COLORS from 'theme/colors';

type ElevationSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

export enum ElevationSizes {
  xs = 'xs',
  sm = 'sm',
  md = 'md',
  lg = 'lg',
  xl = 'xl'
}

export const elevationMap = {
  [ElevationSizes.xs]: {
    _light: {
      lightSurface: {
        boxShadow: `0px 0px 0px 1px ${rgba(COLORS.black.base, 0.16)}, 
        0px 1px 1px ${rgba(COLORS.black.base, 0.04)}`
      },
      darkSurface: {
        boxShadow: `0px 1px 1px ${rgba(COLORS.black.base, 0.04)}, 
        inset 0px 0px 0px 1px ${rgba(COLORS.black.base, 0.24)}`
      }
    },
    _dark: {
      boxShadow: `0px 0px 0px 1px ${rgba(COLORS.silver.lightest, 0.24)}, 
      0px 1px 0px ${rgba(COLORS.black.base, 0.08)}`
    }
  },
  [ElevationSizes.sm]: {
    _light: {
      lightSurface: {
        boxShadow: `0px 0px 0px 1px ${rgba(COLORS.black.base, 0.08)}, 
        0px 2px 2px ${rgba(COLORS.black.base, 0.04)}, 
        0px 2px 4px -2px ${rgba(COLORS.black.base, 0.08)}`
      },
      darkSurface: {
        boxShadow: `0px 2px 2px ${rgba(COLORS.black.base, 0.04)}, 
        0px 2px 4px -2px ${rgba(COLORS.black.base, 0.16)}, 
        inset 0px 0px 0px 0.5px ${rgba(COLORS.black.base, 0.24)}`
      }
    },
    _dark: {
      boxShadow: `0px 0px 0px 1px ${rgba(COLORS.silver.lightest, 0.24)}, 
      0px 2px 2px ${rgba(COLORS.black.base, 0.16)}, 
      0px 2px 4px -2px ${rgba(COLORS.black.base, 0.32)}`
    }
  },
  [ElevationSizes.md]: {
    _light: {
      lightSurface: {
        boxShadow: `0px 0px 0px 1px ${rgba(COLORS.black.base, 0.08)}, 
        0px 2px 2px ${rgba(COLORS.black.base, 0.04)}, 
        0px 4px 8px -2px ${rgba(COLORS.black.base, 0.08)}`
      },
      darkSurface: {
        boxShadow: `0px 2px 2px ${rgba(COLORS.black.base, 0.08)}, 
        0px 4px 8px -2px ${rgba(COLORS.black.base, 0.16)}, 
        inset 0px 0px 0px 1px ${rgba(COLORS.black.base, 0.24)}`
      }
    },
    _dark: {
      boxShadow: `0px 0px 0px 1px ${rgba(COLORS.silver.lightest, 0.24)}, 
      0px 2px 2px ${rgba(COLORS.black.base, 0.16)}, 
      0px 4px 8px -2px ${rgba(COLORS.black.base, 0.32)}`
    }
  },
  [ElevationSizes.lg]: {
    _light: {
      lightSurface: {
        boxShadow: `0px 0px 0px 1px ${rgba(COLORS.black.base, 0.08)}, 
        0px 4px 8px ${rgba(COLORS.black.base, 0.04)}, 
        0px 8px 16px -2px ${rgba(COLORS.black.base, 0.04)}, 
        0px 16px 32px -4px ${rgba(COLORS.black.base, 0.04)}`
      },
      darkSurface: {
        boxShadow: `0px 4px 8px ${rgba(COLORS.black.base, 0.08)}, 
        0px 8px 16px -2px ${rgba(COLORS.black.base, 0.08)}, 
        0px 16px 32px -4px ${rgba(COLORS.black.base, 0.08)}, 
        inset 0px 0px 0px 1px ${rgba(COLORS.black.base, 0.24)}`
      }
    },
    _dark: {
      boxShadow: `0px 0px 0px 1px ${rgba(COLORS.silver.lightest, 0.24)}, 
      0px 4px 8px ${rgba(COLORS.black.base, 0.32)}, 
      0px 8px 16px -2px ${rgba(COLORS.black.base, 0.32)}, 
      0px 16px 32px -4px ${rgba(COLORS.black.base, 0.32)}`
    }
  },
  [ElevationSizes.xl]: {
    _light: {
      lightSurface: {
        boxShadow: `0px 0px 0px 1px ${rgba(COLORS.black.base, 0.08)}, 
        0px 8px 16px ${rgba(COLORS.black.base, 0.02)}, 
        0px 16px 32px -4px ${rgba(COLORS.black.base, 0.04)}, 
        0px 32px 64px -8px ${rgba(COLORS.black.base, 0.04)}`
      },
      darkSurface: {
        boxShadow: `0px 8px 16px ${rgba(COLORS.black.base, 0.04)}, 
        0px 16px 32px -4px ${rgba(COLORS.black.base, 0.08)}, 
        0px 32px 64px -8px ${rgba(COLORS.black.base, 0.08)}, 
        inset 0px 0px 0px 1px ${rgba(COLORS.black.base, 0.24)}`
      }
    },
    _dark: {
      boxShadow: `0px 0px 0px 1px ${rgba(COLORS.silver.lightest, 0.24)}, 
      0px 8px 16px ${rgba(COLORS.black.base, 0.08)}, 
      0px 16px 32px -4px ${rgba(COLORS.black.base, 0.16)}, 
      0px 32px 64px -8px ${rgba(COLORS.black.base, 0.32)}`
    }
  }
};

const elevationLight = (size: ElevationSize) => elevationMap[size]._light;
const elevationDark = (size: ElevationSize) => elevationMap[size]._dark;
type surfaceType = 'lightSurface' | 'darkSurface';

export default function getElevation(
  props: unknown,
  size: ElevationSize,
  surface: surfaceType = 'lightSurface'
): string {
  const isLightSurface = surface === 'lightSurface';
  const surfaceStyle = { ...(isLightSurface ? elevationLight(size).lightSurface : elevationLight(size).darkSurface) };
  const { boxShadow: lightModeBoxShadow } = surfaceStyle;
  const { boxShadow: darkModeBoxShadow } = elevationDark(size);
  return mode(lightModeBoxShadow, darkModeBoxShadow)(props as StyleFunctionProps);
}
